import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { HomiLogo } from '@/components/HomiLogo';
import { useNavigate, Navigate, useLocation, Link } from 'react-router-dom';

export default function Login({ initialMode = 'login' }) {
  const { user, login, register, error } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const location = useLocation();

  const nextParam = new URLSearchParams(location.search).get('next');
  const dest = nextParam && nextParam.startsWith('/') ? nextParam : '/dashboard';

  if (user && user.id) return <Navigate to={dest} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = mode === 'login'
      ? await login(email, password)
      : await register(name, email, password);
    setLoading(false);
    if (res.ok) nav(dest);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-12"
      style={{ background: 'var(--ivory)' }}
    >
      <div className="w-full max-w-md hq-fade-in">
        <div className="flex flex-col items-center mb-10">
          <HomiLogo size={62} showTag={false} />
          <h1 className="hq-h1 text-3xl mt-6" data-testid="app-title">HomiHQ</h1>
          <p className="hq-eyebrow mt-2">Powered by Hired</p>
          <p className="text-xs uppercase tracking-widest mt-1" style={{ color: 'var(--muted)' }}>
            The Agent Operating System
          </p>
        </div>

        <div className="hq-card p-10 relative">
          <div className="text-center mb-8">
            <p className="font-serif text-2xl" style={{ color: 'var(--sage)' }}>
              {mode === 'login' ? 'Welcome back.' : 'Begin your reign.'}
            </p>
            <div className="hq-divider-gold mx-auto w-fit"></div>
          </div>

          <form onSubmit={submit} className="space-y-5">
            {mode === 'register' && (
              <div>
                <label className="hq-label">Full Name</label>
                <input
                  className="hq-input"
                  data-testid="register-name-input"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                />
              </div>
            )}
            <div>
              <label className="hq-label">Email</label>
              <input
                type="email"
                className="hq-input"
                data-testid="login-email-input"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="hq-label">Password</label>
              <input
                type="password"
                className="hq-input"
                data-testid="login-password-input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>

            {error && (
              <div
                data-testid="auth-error"
                className="text-sm px-4 py-3 border"
                style={{ background: '#fbf1eb', borderColor: '#e6c8bb', color: 'var(--danger)' }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="hq-btn-primary w-full mt-2"
            >
              {loading ? 'Working…' : mode === 'login' ? 'Enter HomiHQ' : 'Create Account'}
            </button>
          </form>

          <div className="mt-8 pt-6 text-center" style={{ borderTop: '1px solid var(--line)' }}>
            <button
              type="button"
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              data-testid="switch-auth-mode"
              className="text-xs tracking-widest uppercase"
              style={{ color: 'var(--gold-soft)' }}
            >
              {mode === 'login' ? 'Need an account? Register' : 'Already a member? Sign in'}
            </button>
          </div>
        </div>

        <p className="text-center text-xs mt-6" style={{ color: 'var(--muted)', letterSpacing: '0.06em' }}>
          Quiet luxury for the elite agent.
        </p>

        <div className="text-center mt-6">
          <Link
            to="/pricing"
            data-testid="login-pricing-link"
            className="text-xs uppercase tracking-widest"
            style={{ color: 'var(--gold-soft)' }}
          >
            View Pricing
          </Link>
        </div>
      </div>
    </div>
  );
}
